import { useState } from 'react';

export interface Persona {
  id: string;
  name: string;
  age: number;
  location: string;
  segment: string;
  segmentColor?: string;
  quote: string;
  avatar?: string;        // Emoji fallback (e.g., '👤')
  avatarUrl?: string;     // AI-generated image URL (preferred)
  avatarGenerated?: boolean; // Track if image was AI-generated
  profession?: string;
  metrics?: {
    population?: number;
    loyalty?: number;
    frequency?: number;
  };
  topics?: string[];
  channels?: string[];
  messageFrame?: {
    title: string;
    message: string;
  };
  geography?: string[];
}

interface PersonaCardProps {
  persona: Persona;
  onChat?: (persona: Persona) => void;
  expandable?: boolean;
  defaultExpanded?: boolean;
  className?: string;
}

export function PersonaCard({
  persona,
  onChat,
  expandable = true,
  defaultExpanded = false,
  className = '',
}: PersonaCardProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const segmentColors: Record<string, string> = {
    // Meals de Colombia segments
    'Value Seeker': 'bg-amber-500/20 text-amber-400 border-amber-500/30',
    'Family Buyer': 'bg-primary-500/20 text-primary-400 border-primary-500/30',
    'Premium Indulgent': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    'Young & Impulsive': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    'Traditional Loyalist': 'bg-primary-500/20 text-primary-400 border-primary-500/30',
    // Generic segments
    'Premium Seeker': 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    'Impulse Buyer': 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    'Loyal Traditional': 'bg-primary-500/20 text-primary-400 border-primary-500/30',
    // Legacy segments
    'Health-Conscious': 'bg-success-500/20 text-success-400 border-success-500/30',
    'Price-Sensitive': 'bg-warning-500/20 text-warning-400 border-warning-500/30',
    'Convenience': 'bg-primary-500/20 text-primary-400 border-primary-500/30',
    'Traditional': 'bg-accent-purple/20 text-purple-400 border-purple-500/30',
    'Premium': 'bg-accent-teal/20 text-teal-400 border-teal-500/30',
  };

  const segmentColorClass = segmentColors[persona.segment] || 'bg-surface-600 text-text-secondary border-surface-500';

  return (
    <div className={`card overflow-hidden transition-all duration-200 ${className}`}>
      {/* Compact Header - Always visible */}
      <button
        onClick={() => expandable && setIsExpanded(!isExpanded)}
        className={`w-full p-4 text-left ${expandable ? 'hover:bg-surface-600/30 cursor-pointer' : ''} transition-colors`}
        disabled={!expandable}
      >
        <div className="flex items-center gap-4">
          {/* Avatar - prefer AI-generated image, fallback to emoji */}
          {persona.avatarUrl ? (
            <img
              src={persona.avatarUrl}
              alt={persona.name}
              className="w-12 h-12 rounded-full object-cover flex-shrink-0 border-2 border-surface-500"
            />
          ) : (
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-primary-500/30 to-primary-700/30 flex items-center justify-center text-xl flex-shrink-0">
              {persona.avatar || '👤'}
            </div>
          )}

          {/* Name & Segment */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-semibold text-white">{persona.name}</span>
              <span className="text-sm text-text-secondary">{persona.age} anos</span>
              {persona.location && (
                <span className="text-sm text-text-tertiary">· {persona.location}</span>
              )}
            </div>
            <span className={`inline-flex text-xs px-2 py-0.5 rounded-full border ${segmentColorClass}`}>
              {persona.segment}
            </span>
          </div>

          {/* Compact Metrics */}
          {persona.metrics && (
            <div className="hidden sm:flex items-center gap-4 text-sm">
              {persona.metrics.population !== undefined && (
                <div className="text-center">
                  <div className="font-semibold text-white">{persona.metrics.population}%</div>
                  <div className="text-xs text-text-tertiary">Share</div>
                </div>
              )}
              {persona.metrics.loyalty !== undefined && (
                <div className="text-center">
                  <div className="font-semibold text-primary-400">{persona.metrics.loyalty}%</div>
                  <div className="text-xs text-text-tertiary">Loyalty</div>
                </div>
              )}
            </div>
          )}

          {/* Expand indicator */}
          {expandable && (
            <ChevronIcon isOpen={isExpanded} />
          )}
        </div>
      </button>

      {/* Expanded Content */}
      {isExpanded && (
        <div className="border-t border-surface-400/15 p-4 space-y-4 bg-surface-800/30 animate-in slide-in-from-top-2 duration-200">
          {/* Quote */}
          <div className="relative pl-4 border-l-2 border-primary-500/50">
            <p className="text-text-secondary italic text-sm">"{persona.quote}"</p>
          </div>

          {/* Profession if available */}
          {persona.profession && (
            <div className="flex items-center gap-2 text-sm text-text-secondary">
              <span className="text-primary-400">●</span>
              <span>{persona.profession}</span>
            </div>
          )}

          {/* Metrics Row */}
          {persona.metrics && (
            <div className="grid grid-cols-3 gap-3">
              <MetricBox label="Segment Share" value={`${persona.metrics.population || 0}%`} />
              <MetricBox label="Brand Loyalty" value={`${persona.metrics.loyalty || 0}%`} color="primary" />
              <MetricBox label="Purchase Freq." value={`${persona.metrics.frequency || 0}/yr`} />
            </div>
          )}

          {/* Priority Topics */}
          {persona.topics && persona.topics.length > 0 && (
            <div>
              <h5 className="text-xs font-medium text-text-secondary uppercase tracking-wider mb-2">
                Key Topics
              </h5>
              <div className="flex flex-wrap gap-2">
                {persona.topics.map((topic, i) => (
                  <span key={i} className="text-xs px-2.5 py-1 bg-surface-600 text-text-secondary rounded-full">
                    {topic}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Preferred Channels */}
          {persona.channels && persona.channels.length > 0 && (
            <div>
              <h5 className="text-xs font-medium text-text-secondary uppercase tracking-wider mb-2">
                Preferred Channels
              </h5>
              <div className="flex flex-wrap gap-3">
                {persona.channels.map((channel, i) => (
                  <span key={i} className="flex items-center gap-1.5 text-sm text-text-secondary">
                    <ChannelIcon channel={channel} />
                    {channel}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Message Frame */}
          {persona.messageFrame && (
            <div className="p-3 bg-primary-500/10 border border-primary-500/20 rounded-lg">
              <h5 className="text-xs font-medium text-primary-400 uppercase tracking-wider mb-1">
                {persona.messageFrame.title}
              </h5>
              <p className="text-sm text-text-secondary">{persona.messageFrame.message}</p>
            </div>
          )}

          {/* Geographic Concentration */}
          {persona.geography && persona.geography.length > 0 && (
            <div>
              <h5 className="text-xs font-medium text-text-secondary uppercase tracking-wider mb-2">
                Geographic Focus
              </h5>
              <div className="flex flex-wrap gap-2">
                {persona.geography.map((geo, i) => (
                  <span key={i} className="text-xs px-2 py-1 bg-surface-600/50 text-text-secondary rounded">
                    {geo}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Chat Button */}
          {onChat && (
            <button
              onClick={() => onChat(persona)}
              className="w-full mt-2 py-2.5 bg-primary-500 hover:bg-primary-400 text-white font-medium rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <MessageIcon />
              Chat with {persona.name}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// Compact card for grid display
export function PersonaCardCompact({
  persona,
  onChat,
  className = '',
}: Omit<PersonaCardProps, 'expandable' | 'defaultExpanded'>) {
  const segmentColors: Record<string, string> = {
    // Meals de Colombia segments
    'Value Seeker': 'bg-amber-500/20 text-amber-400',
    'Family Buyer': 'bg-primary-500/20 text-primary-400',
    'Premium Indulgent': 'bg-purple-500/20 text-purple-400',
    'Young & Impulsive': 'bg-emerald-500/20 text-emerald-400',
    'Traditional Loyalist': 'bg-primary-500/20 text-primary-400',
    // Generic segments
    'Premium Seeker': 'bg-purple-500/20 text-purple-400',
    'Impulse Buyer': 'bg-emerald-500/20 text-emerald-400',
    'Loyal Traditional': 'bg-primary-500/20 text-primary-400',
    // Legacy segments
    'Health-Conscious': 'bg-success-500/20 text-success-400',
    'Price-Sensitive': 'bg-warning-500/20 text-warning-400',
    'Convenience': 'bg-primary-500/20 text-primary-400',
    'Traditional': 'bg-accent-purple/20 text-purple-400',
    'Premium': 'bg-accent-teal/20 text-teal-400',
  };

  return (
    <div className={`card p-4 hover:bg-surface-600/30 transition-colors ${className}`}>
      <div className="flex items-start gap-3 mb-3">
        {persona.avatarUrl ? (
          <img
            src={persona.avatarUrl}
            alt={persona.name}
            className="w-10 h-10 rounded-full object-cover flex-shrink-0 border-2 border-surface-500"
          />
        ) : (
          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-primary-500/30 to-primary-700/30 flex items-center justify-center text-lg flex-shrink-0">
            {persona.avatar || '👤'}
          </div>
        )}
        <div className="flex-1 min-w-0">
          <div className="font-medium text-white">{persona.name}</div>
          <div className="text-xs text-text-secondary">
            {persona.age} anos · {persona.location}
          </div>
        </div>
      </div>

      <span className={`inline-flex text-xs px-2 py-0.5 rounded-full mb-3 ${segmentColors[persona.segment] || 'bg-surface-600 text-text-secondary'}`}>
        {persona.segment}
      </span>

      <p className="text-sm text-text-secondary italic mb-4 line-clamp-2">
        "{persona.quote}"
      </p>

      {onChat && (
        <button
          onClick={() => onChat(persona)}
          className="w-full py-2 bg-surface-600 hover:bg-surface-500 text-white text-sm font-medium rounded-lg transition-colors"
        >
          Chat
        </button>
      )}
    </div>
  );
}

function MetricBox({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div className="p-2.5 bg-surface-600/50 rounded-lg text-center">
      <div className={`font-semibold ${color === 'primary' ? 'text-primary-400' : 'text-white'}`}>
        {value}
      </div>
      <div className="text-xs text-text-tertiary">{label}</div>
    </div>
  );
}

function ChevronIcon({ isOpen }: { isOpen: boolean }) {
  return (
    <svg
      className={`w-5 h-5 text-text-secondary transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`}
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
      strokeWidth={2}
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
    </svg>
  );
}

function MessageIcon() {
  return (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
    </svg>
  );
}

function ChannelIcon({ channel }: { channel: string }) {
  // Professional channel icons - no childish emojis
  const icons: Record<string, string> = {
    'Modern Trade': '■',
    'E-commerce': '◆',
    'Traditional': '●',
    'Convenience': '▲',
    'Health Stores': '◇',
    'Online': '◆',
    'Cash & Carry': '□',
    'Supermarkets': '■',
    'Hypermarkets': '■',
    'Street Vendors': '●',
    'Delivery Apps': '◆',
  };
  return <span className="text-primary-400">{icons[channel] || '○'}</span>;
}
